import { motion } from "framer-motion";
import { DIVISIONS } from "@/data/catalog";
import { useLang } from "@/i18n/LanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Plus } from "lucide-react";

function DivisionBlock({ division, flip }) {
  const { t } = useLang();
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`division-${division.id}`}
      className="border-t border-[#3A4536]/15 py-16 md:py-24"
    >
      <div
        className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16 ${
          flip ? "" : ""
        }`}
      >
        {/* Image */}
        <div
          className={`group relative overflow-hidden rounded-[2px] border border-[#3A4536]/10 lg:col-span-5 ${
            flip ? "lg:order-2" : "lg:order-1"
          }`}
        >
          <div className="h-[42vh] overflow-hidden lg:h-[60vh]">
            <img
              src={division.image}
              alt={division.name}
              className="spotlight-img h-full w-full object-cover"
            />
          </div>
          <div
            className="absolute left-5 top-5 flex h-14 w-14 items-center justify-center rounded-full"
            style={{ backgroundColor: division.accent }}
          >
            <span className="font-serif-display text-xl text-[#F4F1EB]">
              {division.index}
            </span>
          </div>
        </div>

        {/* Content */}
        <div
          className={`lg:col-span-7 ${flip ? "lg:order-1" : "lg:order-2"}`}
        >
          <span className="overline" style={{ color: division.accent }}>
            {t("divisions.label")} {division.index}
          </span>
          <h3 className="mt-4 font-serif-display text-5xl font-light leading-[0.95] text-[#1A2118] md:text-6xl">
            {t(`divisions.${division.id}_name`)}
          </h3>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-[#3A4536]">
            {t(`divisions.${division.id}_tag`)}
          </p>

          <button
            data-testid={`btn-explore-${division.id}`}
            onClick={() =>
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })
            }
            className="group mt-8 flex items-center gap-3 rounded-full border border-[#1A2118] px-6 py-3 text-sm text-[#1A2118] transition-colors hover:bg-[#1A2118] hover:text-[#F4F1EB]"
          >
            {t("divisions.enquire")}
            <Plus
              strokeWidth={1.5}
              className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90"
            />
          </button>

          <div className="mt-8 grid grid-cols-1 gap-x-10 md:grid-cols-2">
            {division.subcategories.map((sub) => (
              <div key={sub.name} className="mb-2">
                <Accordion type="single" collapsible>
                  <AccordionItem
                    value={sub.name}
                    className="border-[#3A4536]/15"
                  >
                    <AccordionTrigger
                      data-testid={`subcat-${sub.name}`}
                      className="font-serif-display text-xl text-[#1A2118] hover:no-underline"
                    >
                      {sub.name}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4">
                        {sub.groups.map((g) => (
                          <div key={g.label}>
                            <div className="overline mb-2 text-[#B85D43]">
                              {g.label}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                              {g.items.map((item) => (
                                <span
                                  key={item}
                                  className="text-sm text-[#3A4536]"
                                >
                                  {item}
                                  <span className="ml-4 text-[#3A4536]/30">
                                    /
                                  </span>
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Divisions() {
  const { t } = useLang();
  return (
    <section
      id="divisions"
      data-testid="section-divisions"
      className="mx-auto max-w-[1400px] px-6 py-24 md:px-10 md:py-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-6 max-w-3xl"
      >
        <div className="mb-6 flex items-center gap-3">
          <span className="h-px w-10 bg-[#B85D43]" />
          <span className="overline text-[#B85D43]">{t("divisions.overline")}</span>
        </div>
        <h2 className="font-serif-display text-5xl font-light leading-[0.95] text-[#1A2118] md:text-7xl">
          {t("divisions.h1")}
          <br />
          {t("divisions.h2")}
        </h2>
      </motion.div>

      {DIVISIONS.map((d, i) => (
        <DivisionBlock key={d.id} division={d} flip={i % 2 === 1} />
      ))}
    </section>
  );
}
